import { useState, useCallback, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { RoleSelector } from './RoleSelector';
import { checkEmail } from '../users.api';
import type { User, CreateUserRequest, UpdateUserRequest } from '../users.api';

interface UserFormProps {
  user?: User;
  onSubmit: (data: CreateUserRequest | UpdateUserRequest) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export function UserForm({ user, onSubmit, onCancel, isLoading }: UserFormProps) {
  const { t } = useTranslation();
  const isEdit = !!user;

  const [firstName, setFirstName] = useState(user?.firstName ?? '');
  const [lastName, setLastName] = useState(user?.lastName ?? '');
  const [email, setEmail] = useState(user?.email ?? '');
  const [role, setRole] = useState<'admin' | 'manager' | 'employee' | 'customer'>(
    user?.role ?? 'customer',
  );
  const [gln, setGln] = useState(user?.gln ?? '');

  // Validation state
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [checkingEmail, setCheckingEmail] = useState(false);

  const validateField = useCallback(
    async (field: string, value: string) => {
      const newErrors = { ...errors };

      switch (field) {
        case 'firstName':
          if (!value.trim()) {
            newErrors.firstName = t('validation.required');
          } else {
            delete newErrors.firstName;
          }
          break;
        case 'lastName':
          if (!value.trim()) {
            newErrors.lastName = t('validation.required');
          } else {
            delete newErrors.lastName;
          }
          break;
        case 'email':
          if (!value.trim()) {
            newErrors.email = t('validation.required');
          } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
            newErrors.email = t('validation.invalidEmail');
          } else if (!isEdit) {
            // Check duplicate only for new users
            setCheckingEmail(true);
            try {
              const result = await checkEmail(value);
              if (result.data.exists) {
                newErrors.email = t('users.error.emailExists');
              } else {
                delete newErrors.email;
              }
            } catch {
              delete newErrors.email;
            } finally {
              setCheckingEmail(false);
            }
          } else {
            delete newErrors.email;
          }
          break;
        case 'gln':
          if (role === 'customer' && !value.trim()) {
            newErrors.gln = t('validation.required');
          } else {
            delete newErrors.gln;
          }
          break;
      }

      setErrors(newErrors);
    },
    [errors, isEdit, role, t],
  );

  const handleBlur = (field: string, value: string) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
    validateField(field, value);
  };

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    const data: CreateUserRequest | UpdateUserRequest = {
      firstName,
      lastName,
      ...(isEdit ? {} : { email }),
      role,
      gln: role === 'customer' ? gln : undefined,
    };
    onSubmit(data);
  };

  const fieldStyle = (field: string) => ({
    border: `1px solid ${touched[field] && errors[field] ? 'var(--destructive)' : 'var(--border)'}`,
    backgroundColor: 'var(--background)',
    color: 'var(--foreground)',
  });

  return (
    <form
      onSubmit={handleSubmit}
      data-testid="user-form"
      className="max-w-lg space-y-3.5"
    >
      <div className="flex flex-col gap-[5px]">
        <label
          htmlFor="firstName"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('users.form.firstName')}
        </label>
        <input
          id="firstName"
          data-testid="user-firstname-input"
          type="text"
          value={firstName}
          onChange={(e) => setFirstName(e.target.value)}
          onBlur={() => handleBlur('firstName', firstName)}
          required
          className="rounded-lg px-3 py-2 text-[0.8125rem]"
          style={fieldStyle('firstName')}
        />
        {touched.firstName && errors.firstName && (
          <span data-testid="user-form-name-error" className="text-xs" style={{ color: 'var(--destructive)' }}>
            {errors.firstName}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-[5px]">
        <label
          htmlFor="lastName"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('users.form.lastName')}
        </label>
        <input
          id="lastName"
          data-testid="user-lastname-input"
          type="text"
          value={lastName}
          onChange={(e) => setLastName(e.target.value)}
          onBlur={() => handleBlur('lastName', lastName)}
          required
          className="rounded-lg px-3 py-2 text-[0.8125rem]"
          style={fieldStyle('lastName')}
        />
        {touched.lastName && errors.lastName && (
          <span className="text-xs" style={{ color: 'var(--destructive)' }}>
            {errors.lastName}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-[5px]">
        <label
          htmlFor="userEmail"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('users.form.email')}
        </label>
        <input
          id="userEmail"
          data-testid="user-email-input"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          onBlur={() => handleBlur('email', email)}
          required
          readOnly={isEdit}
          className="rounded-lg px-3 py-2 text-[0.8125rem]"
          style={{
            ...fieldStyle('email'),
            ...(isEdit ? { opacity: 0.6, cursor: 'not-allowed' } : {}),
          }}
        />
        {touched.email && errors.email && (
          <span data-testid="user-form-email-error" className="text-xs" style={{ color: 'var(--destructive)' }}>
            {errors.email}
          </span>
        )}
        {checkingEmail && (
          <span className="text-xs" style={{ color: 'var(--foreground-muted)' }}>
            {t('common.loading')}
          </span>
        )}
      </div>

      <div className="flex flex-col gap-[5px]">
        <label
          htmlFor="role-selector"
          className="text-xs font-medium"
          style={{ color: 'var(--foreground-heading)' }}
        >
          {t('users.form.role')}
        </label>
        <RoleSelector value={role} onChange={setRole} />
      </div>

      {role === 'customer' && (
        <div className="flex flex-col gap-[5px]">
          <label
            htmlFor="userGln"
            className="text-xs font-medium"
            style={{ color: 'var(--foreground-heading)' }}
          >
            {t('users.form.gln')}
          </label>
          <input
            id="userGln"
            data-testid="user-gln-input"
            type="text"
            value={gln}
            onChange={(e) => setGln(e.target.value)}
            onBlur={() => handleBlur('gln', gln)}
            placeholder="8712345678901"
            required
            className="rounded-lg px-3 py-2 text-[0.8125rem]"
            style={fieldStyle('gln')}
          />
          {touched.gln && errors.gln && (
            <span className="text-xs" style={{ color: 'var(--destructive)' }}>
              {errors.gln}
            </span>
          )}
        </div>
      )}

      <div className="flex gap-2 pt-2">
        <button
          type="submit"
          data-testid="user-form-submit"
          disabled={isLoading || checkingEmail}
          className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium text-white transition-all duration-100 disabled:opacity-60"
          style={{
            backgroundColor: 'var(--primary)',
            border: '1px solid var(--primary)',
          }}
        >
          {isLoading ? t('common.loading') : isEdit ? t('users.form.save') : t('users.form.create')}
        </button>
        <button
          type="button"
          data-testid="user-form-cancel"
          onClick={onCancel}
          className="rounded-lg px-4 py-2 text-[0.8125rem] font-medium transition-all duration-100"
          style={{
            border: '1px solid var(--border)',
            backgroundColor: 'var(--background)',
            color: 'var(--foreground)',
          }}
        >
          {t('users.form.cancel')}
        </button>
      </div>
    </form>
  );
}
